
import { createContext, ReactNode, useContext } from 'react';
import { apiV2 } from '../services/api';

interface OutputStock {
    id_transaction: string;
    output_amount: number;
    fk_costs_center_id_cost: string;
    fk_trips_id_trip: string;
    employee: string;
    notes: string;
    created_at: string;
    id_order?: string;
    id_user?: string;
}

interface StockItemRequest {
    item_name: string;
    provider_name: string;
    subsidiary_name: string;
    pages: number;
    limit: number;
}

interface StockOutputsRequest {
    product: string;
    costcenter: string;
    boat: string;
    trip: string;
    pages: number;
    limit: number;
}

interface StockHistoryRequest {
    page?: number;
    pageSize?: number;
    createdAtFrom?: string;
    createdAtTo?: string;
}

interface StockItemsProvidersProps {
    children: ReactNode;
}

interface ResponseProps {
    success: boolean;
    message: string;
}

interface ResponsePropsWithData {
    success: boolean;
    message: string;
    data: any;
}

interface InputReturnToStock {
    id_stock: string;
    id_order: string;
    id_user: string;
    notes: string;
}

interface StockItemsContextData {
    getFilteredStockItems: (request: StockItemRequest) => Promise<ResponsePropsWithData>;
    getOutputsStock: (request: StockOutputsRequest) => Promise<ResponsePropsWithData>;
    getStockHistory: (request?: StockHistoryRequest) => Promise<ResponsePropsWithData>;
    takeItensFromStock: (request: OutputStock) => Promise<ResponseProps>;
    returnItensToStock: (request: InputReturnToStock) => Promise<ResponseProps>;
}

const StockContext = createContext<StockItemsContextData>(
    {} as StockItemsContextData
);

function isEmptyFilter(value: unknown) {
  return value === undefined || value === null || value === '' || value === "''";
}

function removeEmptyFilters(request: Record<string, unknown>) {
  return Object.entries(request).reduce<Record<string, unknown>>((acc, [key, value]) => {
    if (!isEmptyFilter(value)) acc[key] = value;
    return acc;
  }, {});
}

function isTechnicalMessage(message: unknown) {
  if (typeof message !== 'string') return true;

  return /^(cannot\s+(get|post|put|patch|delete)|axioserror|error:|network error|internal server error|failed with status code)/i.test(message.trim()) ||
    /<\/?[a-z][\s\S]*>/i.test(message) ||
    /\/api\/v\d/i.test(message);
}

function translateKnownApiMessage(message: unknown) {
  if (typeof message !== 'string') return undefined;

  const normalizedMessage = message.trim();

  if (/^validation failed\.?$/i.test(normalizedMessage)) {
    return 'Não foi possível validar os dados enviados. Revise os campos e tente novamente.';
  }

  if (/expected a valid date in yyyy-mm-dd format/i.test(normalizedMessage)) {
    return 'Informe as datas no formato AAAA-MM-DD.';
  }

  if (/invalid uuid/i.test(normalizedMessage)) {
    return 'Selecione novamente os registros necessários e tente outra vez.';
  }

  if (/too small|greater than 0|positive/i.test(normalizedMessage)) {
    return 'Informe uma quantidade maior que zero.';
  }

  if (/number must be (greater than or equal to|less than or equal to)/i.test(normalizedMessage) ||
    /(page|pagesize).*(greater than|less than|min|max|integer|number)/i.test(normalizedMessage)) {
    return 'Informe uma página e uma quantidade por página válidas.';
  }

  if (/^stock transaction not found\.?$/i.test(normalizedMessage)) {
    return 'O item de estoque selecionado não foi encontrado ou não está mais disponível.';
  }

  if (/^insufficient stock balance\.?$/i.test(normalizedMessage)) {
    return 'A quantidade solicitada é maior que o saldo disponível em estoque.';
  }

  if (/^stock output reference not found\.?$/i.test(normalizedMessage)) {
    return 'Centro de custo, viagem ou receptor não encontrado para esta retirada.';
  }

  if (/^stock output not found\.?$/i.test(normalizedMessage)) {
    return 'A movimentação de estoque selecionada não foi encontrada.';
  }

  if (/^derived stock order not found\.?$/i.test(normalizedMessage)) {
    return 'O pedido gerado por esta retirada não foi encontrado.';
  }

  if (/^you do not have permission to perform this action\.?$/i.test(normalizedMessage)) {
    return 'Você não tem permissão para realizar esta ação.';
  }

  if (/^unauthorized\.?$/i.test(normalizedMessage)) {
    return 'Sessão expirada ou não autorizada. Faça login novamente.';
  }

  return undefined;
}

function getApiMessage(response: any) {
  return response?.data?.message || response?.message || response?.data?.errors?.message || response?.data?.errors?.toString?.();
}

function isSuccessfulResponse(response: any) {
  return response?.success !== false && response?.status >= 200 && response?.status < 300;
}

function normalizeCivilDateForApi(value: string) {
  if (/^\d{4}-\d{2}-\d{2}$/.test(value)) {
    return `${value}T00:00:00.000Z`;
  }

  return value;
}

function normalizeStockOutputPayload(transaction: OutputStock) {
  return {
    ...transaction,
    output_amount: Number(transaction.output_amount),
    created_at: normalizeCivilDateForApi(transaction.created_at)
  };
}

function withLegacyOutputPagination(responseData: any) {
  return {
    ...responseData,
    info: responseData?.info || [{ pages: responseData?.pages || 1, qty: responseData?.qty || 0 }]
  };
}

export function StockProvider({ children }: StockItemsProvidersProps) {

  async function getFilteredStockItems(request: StockItemRequest) {
    const params = { ...request };
    const response: any = await apiV2.get('/stock', { params });

    if (isSuccessfulResponse(response)) {
      return {
        success: true,
        message: "",
        data: response.data
      };
    }
    const apiMessage = getApiMessage(response);
    const translatedMessage = translateKnownApiMessage(apiMessage);

    return {
      success: false,
      message: translatedMessage || (isTechnicalMessage(apiMessage) ? "Erro ao consultar itens do estoque." : apiMessage),
      data: []
    };
  }

  async function getOutputsStock(request: StockOutputsRequest) {
    const params = { ...request };
    const response: any = await apiV2.get('/stockoutputs', { params });

    if (isSuccessfulResponse(response)) {
      return {
        success: true,
        message: "",
        data: withLegacyOutputPagination(response.data)
      };
    }

    const apiMessage = getApiMessage(response);
    const translatedMessage = translateKnownApiMessage(apiMessage);

    return {
      success: false,
      message: translatedMessage || (isTechnicalMessage(apiMessage) ? "Erro ao consultar movimentações do estoque." : apiMessage),
      data: []
    };
  }

  async function getStockHistory(request: StockHistoryRequest = {}) {
    const response: any = await apiV2.get('/audit-logs', { params: removeEmptyFilters({ ...request, isStocked: true }) });
    const success = response?.success !== false && response?.status >= 200 && response?.status < 300;

    if (success) {
      return {
        success: true,
        message: "",
        data: response.data
      };
    }

    const apiMessage = getApiMessage(response);
    const translatedMessage = translateKnownApiMessage(apiMessage);

    return {
      success: false,
      message: translatedMessage || (isTechnicalMessage(apiMessage) ? "Erro ao consultar histórico do estoque." : apiMessage),
      data: []
    };
  }

  async function takeItensFromStock(transaction: OutputStock) {
    const response: any = await apiV2.post('/outputstock', normalizeStockOutputPayload(transaction));

    if (isSuccessfulResponse(response)) {
      // setRefreshOnDelete(oldKey => oldKey + 1);
      return {
        success: true,
        message: "Item retirado do estoque com sucesso!"
      };
    }
    const apiMessage = getApiMessage(response);
    const translatedMessage = translateKnownApiMessage(apiMessage);

    return {
      success: false,
      message: translatedMessage || (isTechnicalMessage(apiMessage) ? "Erro ao retirar item do estoque." : apiMessage)
    };
  }

  async function returnItensToStock(request: InputReturnToStock) {
    const response: any = await apiV2.put('/outputstock', { ...request });

    if (isSuccessfulResponse(response)) {
      // setRefreshOnDelete(oldKey => oldKey + 1);
      return {
        success: true,
        message: "Item devolvido ao estoque com sucesso!"
      };
    }
    const apiMessage = getApiMessage(response);
    const translatedMessage = translateKnownApiMessage(apiMessage);

    return {
      success: false,
      message: translatedMessage || (isTechnicalMessage(apiMessage) ? "Erro ao devolver item ao estoque." : apiMessage)
    };
  }

  return (
    <StockContext.Provider value={{ getFilteredStockItems, getOutputsStock, takeItensFromStock, returnItensToStock, getStockHistory }}>
      {children}
    </StockContext.Provider>
  );
}

export function UseStock() {
  const context = useContext(StockContext);
  return context;
}
